import type { Ref } from 'vue';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { useCartStore } from '@/stores/cart';
import { useCheckoutStore } from '@/stores/checkoutStore';
import { useDoorsaleStore } from '@/stores/doorsaleStore';
import { useSeatmapStore } from '@/stores/seatmapStore';
import { advanceDoorsaleEvent } from '@/utils/advanceDoorsaleEvent';

type DoorsaleEventStep = 'doorsale-tickets' | 'doorsale-upsale' | 'doorsale-seats';

const EVENT_STEPS: DoorsaleEventStep[] = [
	'doorsale-tickets',
	'doorsale-upsale',
	'doorsale-seats',
];

export function useDoorsaleNavigation(eventId: Ref<number | string>) {
	const router = useRouter();
	const cart = useCartStore();
	const checkoutStore = useCheckoutStore();
	const doorsaleStore = useDoorsaleStore();
	const seatmapStore = useSeatmapStore();

	function isStepActive(step: DoorsaleEventStep): boolean {
		switch (step) {
			case 'doorsale-upsale': {
				const available = cart.getAvailableUpsaleTicketsForBasket();
				return checkoutStore.upsaleTickets.some(t => available.includes(Number(t.id)));
			}
			case 'doorsale-seats':
				// Also true when a confirmed snapshot exists for this event — handles
				// the case where seatmapActive is momentarily false after
				// clearCheckoutData() cleared checkoutStore.tickets before the
				// subscribeevent WS response re-populates them.
				return cart.seatmapActive || seatmapStore.hasDoorsaleEventSnapshot(eventId.value);
			default:
				return true;
		}
	}

	const activeSteps = computed(() =>
		EVENT_STEPS.filter(step => isStepActive(step))
	);

	function nextStep(currentStep: DoorsaleEventStep): void {
		const currentIndex = activeSteps.value.indexOf(currentStep);
		const nextStepName = activeSteps.value[currentIndex + 1];
		if (nextStepName) {
			router.push({ name: nextStepName, params: { eventId: eventId.value } });
		} else {
			advanceDoorsaleEvent(router, doorsaleStore, eventId.value);
		}
	}

	function previousStep(currentStep: DoorsaleEventStep): void {
		const currentIndex = activeSteps.value.indexOf(currentStep);
		if (currentIndex > 0) {
			router.push({ name: activeSteps.value[currentIndex - 1], params: { eventId: eventId.value } });
			return;
		}
		// First step of this event — go to the previous event's tickets, or the event list
		const ids = doorsaleStore.selectedEventIds;
		const pos = ids.indexOf(eventId.value);
		if (pos > 0) {
			router.push({ name: 'doorsale-tickets', params: { eventId: ids[pos - 1] } });
		} else {
			router.push({ name: 'doorsale-events' });
		}
	}

	return {
		activeSteps,
		isStepActive,
		nextStep,
		previousStep,
	};
}
